import { decorStampPlaceIds, placeStampById } from '../../data/placeStamps'
import { placesMock } from '../../data/places'
import { PlaceStampImage } from '../stamps/PlaceStampImage'
import { useT } from '../../i18n/t'

const DEMO_VERIFIED_AT: Partial<Record<number, string>> = {
  1: '11.06.2026',
  2: '23.04.2026',
  3: '18.05.2026',
  5: '27.10.2026',
  6: '02.08.2026',
  9: '14.09.2026',
}

const verifiedDemoIds = new Set<number>(decorStampPlaceIds)

type HeroPassportStampsCardProps = {
  className?: string
}

export function HeroPassportStampsCard({ className = '' }: HeroPassportStampsCardProps) {
  const { t } = useT()
  const verifiedCount = decorStampPlaceIds.length
  const totalCount = placesMock.length

  return (
    <div
      className={`rounded-lg border border-white/20 bg-[#faf8f4] p-3.5 text-neutral-900 shadow-[0_24px_60px_rgba(0,0,0,0.42)] sm:p-4 md:p-5 ${className}`}
      role="img"
      aria-label={t('landing.heroLaunch.passportCard.aria')}
    >
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-900/55 sm:text-[11px]">
            {t('panel.welcome.stamps.eyebrow')}
          </div>
          <div className="display mt-1 text-[17px] font-bold leading-tight text-neutral-900 sm:text-[20px] md:text-[22px]">
            {t('panel.welcome.stamps.title')}
          </div>
        </div>
        <div className="text-[12px] font-extrabold tabular-nums text-neutral-900 sm:text-[13px]">
          {verifiedCount}
          <span className="text-neutral-900/35">/{totalCount}</span>
        </div>
      </div>

      <ul
        className="mt-3 grid grid-cols-4 gap-1.5 sm:mt-4 sm:gap-2 md:gap-2.5"
        aria-hidden
      >
        {placesMock.map((place) => {
          const verified = verifiedDemoIds.has(place.id)
          const stampSrc = placeStampById[place.id] ?? null
          const dateLabel = DEMO_VERIFIED_AT[place.id] ?? null

          return (
            <li key={place.id} className="min-w-0">
              <div className="flex flex-col items-center text-center">
                <div className="h-11 w-11 sm:h-12 sm:w-12 md:h-14 md:w-14">
                  {verified && stampSrc ? (
                    <PlaceStampImage
                      src={stampSrc}
                      alt=""
                      loading="eager"
                      width={56}
                      height={56}
                      padClassName="h-full w-full"
                      imageClassName="h-[82%] w-[82%] object-contain"
                    />
                  ) : (
                    <div
                      className="h-full w-full rounded-lg border border-dashed border-neutral-900/12 bg-neutral-900/[0.03]"
                      aria-hidden
                    />
                  )}
                </div>
                <div
                  className={[
                    'mt-0.5 line-clamp-2 text-[8px] font-extrabold leading-snug sm:text-[9px] md:text-[10px]',
                    verified ? 'text-neutral-900' : 'text-neutral-900/45',
                  ].join(' ')}
                >
                  {place.name}
                </div>
                <div className="mt-0.5 text-[7px] font-semibold uppercase tracking-[0.12em] text-neutral-900/40 sm:text-[8px]">
                  {verified && dateLabel ? dateLabel : '—'}
                </div>
              </div>
            </li>
          )
        })}
      </ul>

      <p className="mt-2.5 text-center text-[9px] font-medium text-neutral-900/45 sm:text-[10px]">
        {t('landing.heroLaunch.passportCard.hint')}
      </p>
    </div>
  )
}
